import {customThemeConstants} from '@hconnect/uikit/src/lib2'
import {Box, useTheme} from '@mui/material'
import {Chart as ChartJS, ArcElement} from 'chart.js'
import {FC, useMemo} from 'react'
import {Doughnut} from 'react-chartjs-2'
import {useTranslation} from 'react-i18next'

import {DOWNTIME_ANALYTICS_WIDGET_NAMESPACE} from '../consts'

import {RcfaSummaryExtended} from './RcfaSummaryExtended'

type RcfaSummaryWithProgressChartProps = {
  longKilnStoppageRcfaCount: number
  lateLongKilnStoppageRcfaCount: number
  overdueKilnStoppageRcfaCount: number
  totalOverdueKilnStoppageRcfaCount: number
  stoppageRcfaCount: number
  longKilnStoppageCount: number
  timeRangeLabel: string
}

ChartJS.register(ArcElement)

export const RcfaSummaryWithProgressChart: FC<RcfaSummaryWithProgressChartProps> = ({
  longKilnStoppageRcfaCount,
  overdueKilnStoppageRcfaCount,
  totalOverdueKilnStoppageRcfaCount,
  lateLongKilnStoppageRcfaCount,
  stoppageRcfaCount,
  longKilnStoppageCount,
  timeRangeLabel
}) => {
  const theme = useTheme()
  const {t} = useTranslation(DOWNTIME_ANALYTICS_WIDGET_NAMESPACE)

  const MANDATORY_RCFA_COLOR = customThemeConstants().palette.buttonFillColor
  const RCFA_EXISTING_COLOR = customThemeConstants().palette.barChartDefault

  const chartOptions = {
    cutout: '70%',
    plugins: {
      legend: {display: false},
      tooltip: {
        enabled: false
      }
    }
  }

  const chartCenterText = useMemo(() => {
    return {
      id: 'chart_center_text',
      afterDatasetsDraw(chart) {
        const {ctx} = chart

        const xBase = chart.getDatasetMeta(0).data[0].x
        const yBase = chart.getDatasetMeta(0).data[0].y
        ctx.save()

        ctx.font = 'normal 300 22px/34 Inter'
        ctx.textAlign = 'center'
        ctx.fillStyle = theme.palette.text.primary
        ctx.textBaseLine = 'bottom'

        if (
          typeof longKilnStoppageRcfaCount === 'number' &&
          typeof longKilnStoppageCount === 'number'
        ) {
          ctx.fillText(`${longKilnStoppageRcfaCount}/${longKilnStoppageCount}`, xBase, yBase)
        }
        ctx.font = 'normal 400 11px/16 Inter'
        ctx.textAlign = 'center'
        ctx.fillStyle = theme.palette.secondary.main
        ctx.textBaseLine = 'bottom'
        ctx.fillText(t('rcfaSummary.label.mandatoryRCFA'), xBase, yBase + 20)
      }
    }
  }, [theme, longKilnStoppageRcfaCount, longKilnStoppageCount, t])

  const chartData = useMemo(() => {
    if (!longKilnStoppageCount) {
      return {
        datasets: [
          {
            data: [1, 0],
            backgroundColor: [MANDATORY_RCFA_COLOR, RCFA_EXISTING_COLOR]
          }
        ]
      }
    }

    const calculatePercentageParts = (
      longKilnStoppageRcfaCount: number,
      longKilnStoppageCount: number
    ): number[] => {
      const used = (longKilnStoppageRcfaCount / longKilnStoppageCount) * 100
      const remaining = 100 - used

      return [remaining, used]
    }

    return {
      datasets: [
        {
          data: calculatePercentageParts(longKilnStoppageRcfaCount, longKilnStoppageCount),
          backgroundColor: [MANDATORY_RCFA_COLOR, RCFA_EXISTING_COLOR]
        }
      ]
    }
  }, [longKilnStoppageRcfaCount, longKilnStoppageCount, MANDATORY_RCFA_COLOR, RCFA_EXISTING_COLOR])

  return (
    <>
      <Box height={180} display="flex" justifyContent="center" alignItems="center">
        <Box height="inherit" overflow="hidden">
          <Doughnut data={chartData} options={chartOptions} plugins={[chartCenterText]} />
        </Box>
      </Box>
      <RcfaSummaryExtended
        longKilnStoppageRcfaCount={longKilnStoppageRcfaCount}
        overdueKilnStoppageRcfaCount={overdueKilnStoppageRcfaCount}
        totalOverdueKilnStoppageRcfaCount={totalOverdueKilnStoppageRcfaCount}
        lateLongKilnStoppageRcfaCount={lateLongKilnStoppageRcfaCount}
        stoppageRcfaCount={stoppageRcfaCount}
        timeRangeLabel={timeRangeLabel}
      />
    </>
  )
}
